import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getImageUrl } from '../../config';
import { formatDateTime } from '../../lib/formatDate';
import { APP_STATUS_LABEL } from '../../lib/requestStatus';

const STATUS_LABEL = APP_STATUS_LABEL;

function Avatar({ name, photoUrl }) {
  if (photoUrl) {
    return <img className="ba-chip-avatar" src={getImageUrl(photoUrl)} alt="" />;
  }
  return (
    <span className="ba-chip-avatar ba-chip-avatar--fallback" aria-hidden="true">
      {(name || '?').charAt(0).toUpperCase()}
    </span>
  );
}


// Tools shown as name-only chips on the card; the rest collapse into a "+N".
const MAX_TOOL_CHIPS = 5;

// The mandatory, server-injected Google SSO tool — rendered with its logo and a
// distinct style on the request cards. Matches SSO_TOOL_NAME in the server.
const SSO_TOOL_NAME = 'Google SSO';
const SSO_TOOL_ICON = '/images/google-sso.png';
const isSsoTool = (t) => t.is_custom && (t.tool_name || '').toLowerCase() === SSO_TOOL_NAME.toLowerCase();

const ROLE_FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'owned', label: 'Owned' },
  { key: 'collab', label: 'Collaborator' },
];

export default function MyAppRequests({ apps, onCancel, onEdit, onBuild }) {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');

  const counts = useMemo(() => {
    let owned = 0;
    let collab = 0;
    for (const a of apps) {
      if (a.is_creator === true) owned += 1;
      else collab += 1;
    }
    return { all: apps.length, owned, collab };
  }, [apps]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return apps.filter((a) => {
      if (roleFilter === 'owned' && a.is_creator !== true) return false;
      if (roleFilter === 'collab' && a.is_creator === true) return false;
      if (q && !(a.name || '').toLowerCase().includes(q)) return false;
      return true;
    });
  }, [apps, query, roleFilter]);

  if (!apps.length) {
    return (
      <div className="ba-empty-state">
        <span className="ba-empty-icon" aria-hidden="true">
          <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 2 2 7l10 5 10-5-10-5Z" />
            <path d="m2 17 10 5 10-5" />
            <path d="m2 12 10 5 10-5" />
          </svg>
        </span>
        <h3 className="ba-empty-title">Got an idea? Build it.</h3>
        <p className="ba-empty-lead">
          Turn the tools and data you already use into an internal app — no waiting
          on a backlog. Pick your tools, describe how you&apos;ll use them, and we&apos;ll
          provision the access. The apps you ship can land in the catalog for the
          whole team.
        </p>
        {onBuild && (
          <button type="button" className="btn btn-primary ba-empty-cta" onClick={onBuild}>
            + Build an app
          </button>
        )}
        <p className="ba-empty-hint">Most requests are approved within a day.</p>
      </div>
    );
  }
  const open = (id) => navigate(`/build-app/${id}`);
  return (
    <div className="ba-list-wrap page-fill">
      <div className="ba-list-toolbar">
        <div className="ba-search">
          <svg className="ba-search-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <circle cx="11" cy="11" r="7" />
            <path d="m21 21-4.3-4.3" />
          </svg>
          <input
            type="text"
            className="ba-search-input"
            placeholder="Search by name…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            aria-label="Search app requests by name"
          />
          {query && (
            <button
              type="button"
              className="ba-search-clear"
              onClick={() => setQuery('')}
              aria-label="Clear search"
            >
              ×
            </button>
          )}
        </div>
        <div className="ba-role-filter" role="group" aria-label="Filter by role">
          {ROLE_FILTERS.map((f) => (
            <button
              key={f.key}
              type="button"
              className={`ba-role-filter-btn${roleFilter === f.key ? ' is-active' : ''}`}
              aria-pressed={roleFilter === f.key}
              onClick={() => setRoleFilter(f.key)}
            >
              {f.label}
              <span className="ba-role-filter-count">{counts[f.key]}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="page-scroll-body">
      {filtered.length === 0 ? (
        <div className="ba-list-noresults">
          <p>No requests match your filters.</p>
          <button
            type="button"
            className="btn btn-secondary btn-small"
            onClick={() => { setQuery(''); setRoleFilter('all'); }}
          >
            Clear filters
          </button>
        </div>
      ) : (
    <ul className="ba-list">
      {filtered.map((app) => {
        const isCreator = app.is_creator === true;
        const collaborators = app.collaborators || [];
        const createdAt = formatDateTime(app.created_at);
        const isActiveLike = ['provisioning', 'active', 'publish_requested', 'published'].includes(app.status);
        // Pin the mandatory Google SSO chip first so its logo leads each card.
        const rawTools = app.tools || [];
        const tools = [...rawTools].sort((a, b) => (isSsoTool(b) ? 1 : 0) - (isSsoTool(a) ? 1 : 0));
        const visibleTools = tools.slice(0, MAX_TOOL_CHIPS);
        const extraTools = tools.length - visibleTools.length;
        return (
          <li
            key={app.id}
            className={`ba-card ba-card--clickable ba-card--${app.status}`}
            role="button"
            tabIndex={0}
            onClick={() => open(app.id)}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); open(app.id); } }}
          >
            <div className="ba-card-head">
              <div className="ba-card-headings">
                <h4 className="ba-card-name" title={app.name}>{app.name}</h4>
                {app.description && (
                  <p className="ba-desc ba-desc--clamp" title={app.description}>{app.description}</p>
                )}
              </div>
              <div className="ba-card-head-tags">
                <span className={`ba-role-tag ba-role-tag--${isCreator ? 'owner' : 'collab'}`}>
                  {isCreator ? 'Owner' : 'Collaborator'}
                </span>
                <span className={`ba-badge ba-badge-${app.status}`}>{STATUS_LABEL[app.status] || app.status}</span>
              </div>
            </div>

            {tools.length > 0 && (
              <ul className="ba-tool-chips" aria-label="Tools">
                {visibleTools.map((t, i) => {
                  const sso = isSsoTool(t);
                  return (
                    <li
                      key={t.tool_id != null ? `t-${t.tool_id}` : `c-${i}`}
                      className={`ba-tool-chip${sso ? ' ba-tool-chip--sso' : t.is_custom ? ' ba-tool-chip--custom' : ''}`}
                      title={t.usage_note ? `${t.tool_name} — ${t.usage_note}` : t.tool_name}
                    >
                      {sso ? (
                        <img className="ba-tool-chip-icon" src={getImageUrl(SSO_TOOL_ICON)} alt="" />
                      ) : !t.is_custom && (
                        t.tool_icon_url
                          ? <img className="ba-tool-chip-icon" src={getImageUrl(t.tool_icon_url)} alt="" />
                          : <span className="ba-tool-chip-icon ba-tool-chip-icon--fallback" aria-hidden="true">{(t.tool_name || '?').charAt(0).toUpperCase()}</span>
                      )}
                      <span className="ba-tool-chip-name">{t.tool_name}</span>
                    </li>
                  );
                })}
                {extraTools > 0 && (
                  <li className="ba-tool-chip ba-tool-chip--more">+{extraTools} more</li>
                )}
              </ul>
            )}

            {app.status === 'rejected' && app.admin_notes && (
              <p className="ba-reject">Reason: {app.admin_notes}</p>
            )}

            <div className="ba-card-foot">
              <span className="ba-meta-creator">
                <Avatar name={app.requester_name} photoUrl={app.requester_photo_url} />
                <span className="ba-meta-text">
                  {isCreator ? 'Created by you' : `Created by ${app.requester_name || 'someone'}`}
                  {createdAt && <span className="ba-meta-time"> · {createdAt}</span>}
                </span>
              </span>
              {collaborators.length > 0 && (
                <span className="ba-foot-team" title={collaborators.map((c) => c.name).join(', ')}>
                  <span className="ba-foot-avatars">
                    {collaborators.slice(0, 3).map((c) => (
                      <span key={c.user_id} className="ba-foot-avatar">
                        <Avatar name={c.name} photoUrl={c.photo_url} />
                      </span>
                    ))}
                  </span>
                  {collaborators.length > 3 && <span className="ba-foot-team-more">+{collaborators.length - 3}</span>}
                </span>
              )}
              <span className="ba-card-foot-spacer" />
              {isCreator && app.status === 'pending' && onEdit && (
                <button
                  type="button"
                  className="ba-edit"
                  onClick={(e) => { e.stopPropagation(); onEdit(app.id); }}
                >
                  Edit
                </button>
              )}
              {isCreator && app.status === 'pending' && (
                <button
                  type="button"
                  className="ba-cancel"
                  onClick={(e) => { e.stopPropagation(); onCancel(app.id); }}
                >
                  Cancel
                </button>
              )}
              <span className="ba-card-open">
                {isActiveLike ? 'View references' : 'View details'} →
              </span>
            </div>
          </li>
        );
      })}
    </ul>
      )}
      </div>
    </div>
  );
}
